import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import JsonLd from '../../components/JsonLd/JsonLd';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import CoberturaNota from '../../components/CoberturaNota/CoberturaNota';
import './CiberseguridadCumplimientoPage.css';

const PAGE_URL = 'https://e-asy.es/servicios/ciberseguridad-cumplimiento';

const FAQ_ITEMS = [
  {
    question: '¿Esto es un servicio de soporte IT (incidencias, impresoras, usuarios)?',
    answer:
      'No. No ofrecemos un departamento IT externo genérico. Ciberseguridad y cumplimiento cubre auditoría normativa (RGPD, AI Act, ENS si aplica), accesos, backups de continuidad, formación del equipo y el perímetro cuando el dato es sensible.',
  },
  {
    question: '¿Trabajáis con datos de menores, dependencia o violencia de género?',
    answer:
      'Sí. Es el diferencial: implantamos y protegemos sistemas —incluido Vulpin Social / Care— en entidades sociosanitarias. El enfoque es categoría especial de datos, no un pack de antivirus.',
  },
  {
    question: '¿Incluye el AI Act o solo el RGPD?',
    answer:
      'Los dos, cuando usáis o vais a usar IA. La auditoría cubre tratamientos RGPD y usos de IA; la alfabetización del Art. 4 se desarrolla en la línea de Cumplimiento IA, coordinada con esta.',
  },
  {
    question: '¿Y si el dato no puede salir a la nube pública?',
    answer:
      'Eso entra en el mismo criterio de seguridad: on-premise o nube privada, no una API genérica. El despliegue de modelos está en IA privada / soberana; aquí definimos el perímetro, los accesos y las copias.',
  },
];

const CiberseguridadCumplimientoPage = ({ darkMode, toggleDarkMode }) => {
  const beneficios = [
    {
      titulo: 'Datos de categoría especial',
      descripcion:
        'Protección pensada para menores, dependencia, violencia de género y sanidad: el dato no se trata como un Excel más.',
    },
    {
      titulo: 'Cumplir sin paralizar',
      descripcion:
        'RGPD, AI Act y ENS cuando aplica, con medidas que el equipo puede seguir en el día a día. Sin teatro documental.',
    },
    {
      titulo: 'Quién ve qué, y por qué',
      descripcion:
        'Accesos, roles y permisos alineados con el expediente real. Menos cuentas compartidas y menos “lo tiene todo el mundo”.',
    },
    {
      titulo: 'Continuidad operativa',
      descripcion:
        'Backups cifrados, pruebas de restauración y un plan si mañana el sistema no arranca. Seguridad es también no perder el trabajo.',
    },
    {
      titulo: 'El eslabón humano',
      descripcion:
        'Formación y concienciación para el equipo que abre el correo y atiende a la persona usuaria. La normativa no se cumple sola.',
    },
    {
      titulo: 'Cuando el dato no puede ir a la nube',
      descripcion:
        'Criterio para decidir on-premise, nube privada o API pública. Si el expediente no puede salir, no lo empujamos a ChatGPT.',
    },
  ];

  const serviciosIncluidos = [
    {
      titulo: 'Auditoría de seguridad y cumplimiento',
      descripcion:
        'Diagnóstico de dónde están los datos, quién accede, qué exige RGPD, AI Act y ENS si aplica, y qué hay que arreglar primero.',
      caracteristicas: [
        'Mapa de tratamientos y categorías especiales de datos',
        'Huecos de RGPD: bases legales, encargados, EIPD, registros',
        'AI Act: usos de IA, riesgos y alfabetización (Art. 4)',
        'ENS u otras exigencias de administración pública, si aplica',
        'Informe priorizado: riesgo real, no checklist de 80 páginas',
      ],
    },
    {
      titulo: 'Gestión de accesos, permisos y políticas',
      descripcion:
        'Que cada persona vea lo que le toca en el expediente, el ERP o el servidor. Políticas que se pueden auditar, no un PDF olvidado.',
      caracteristicas: [
        'Usuarios, roles y control de accesos basado en el puesto',
        'Autenticación multifactor y políticas de contraseñas',
        'Gestión de dominios y dispositivos (MDM) cuando hace falta',
        'Revisión de cuentas privilegiadas y accesos “de siempre”',
        'Políticas de seguridad que el equipo entiende y aplica',
      ],
    },
    {
      titulo: 'Backups, continuidad y recuperación',
      descripcion:
        'No es “soporte IT genérico”: es que un ransomware, un disco o un error humano no se lleve expedientes, nóminas o la historia clínica.',
      caracteristicas: [
        'Backups automáticos diarios y cifrados',
        'Copia fuera del mismo servidor (nube segura o destino aislado)',
        'Planes de contingencia y recuperación ante desastres',
        'Pruebas periódicas de restauración, no solo “el backup corre”',
        'Actualizaciones de seguridad y monitoreo de la infraestructura',
      ],
    },
    {
      titulo: 'Formación y concienciación del equipo',
      descripcion:
        'Sustituye a la “formación digital” genérica: phishing, datos sensibles, qué se puede pegar en una IA y qué no, y cómo actuar si hay un incidente.',
      caracteristicas: [
        'Sesiones adaptadas a atención, administración y dirección',
        'Simulacros de phishing y buenas prácticas de correo',
        'Uso de IA y herramientas cloud con criterio RGPD / AI Act',
        'Qué hacer ante una fuga o un acceso indebido',
        'Documentación lista para acreditar alfabetización y formación',
      ],
    },
  ];

  const casosPorSector = [
    {
      titulo: 'Sociosanitario y tercer sector',
      descripcion:
        'Entidades que trabajan con dependencia, menores o violencia de género. Datos de categoría especial, a menudo en Vulpin Social / Care, con obligaciones que un antivirus no cubre.',
      casos: [
        'Residencia o SAD: expedientes de personas en situación de dependencia con accesos por turno, no una carpeta compartida.',
        'Infancia y familia: historiales de menores con trazabilidad de quién consulta y copias que se pueden restaurar si hay un incidente.',
        'Recursos de mujer: datos de violencia de género que no pueden ir a una IA pública ni a un backup sin cifrar en el mismo despacho.',
      ],
      extra: (
        <p className="sector-extra">
          Si operáis en este ámbito, encaja con{' '}
          <Link to="/tercer-sector">nuestra línea de tercer sector</Link> y con{' '}
          <Link to="/vulpin">Vulpin Suite</Link> (Social / Care).
        </p>
      ),
    },
    {
      titulo: 'Pymes con datos de clientes',
      descripcion:
        'Comercio, servicios profesionales o clínicas pequeñas: clientes, facturación, RRHH y, a veces, salud. El riesgo no es “un hacker de película”; es un correo, un portátil o un acceso que nadie revocó.',
      casos: [
        'Despacho o clínica: historiales y datos de contacto con permisos por rol y registro de tratamientos RGPD al día.',
        'Pyme con ecommerce o CRM: clientes y pagos protegidos, sin mezclar copias de producción en el portátil de dirección.',
        'Equipo híbrido: políticas claras de qué se sincroniza en la nube, qué se cifra y quién puede exportar listados.',
      ],
    },
    {
      titulo: 'IA soberana: el dato no sale a la nube pública',
      descripcion:
        'Cuando hay menores, dependencia, sanidad o cláusula anti-cloud, “poner ChatGPT encima del expediente” no es una opción. La ciberseguridad aquí es decidir dónde corre el modelo.',
      casos: [
        'Resumir expedientes o informes sin enviar el contenido a una API genérica: modelo on-premise o nube privada europea.',
        'Contrato con administración o entidad social que prohíbe sacar el dato: arquitectura y evidencias de que el perímetro se respeta.',
        'Alfabetización AI Act (Art. 4) para el equipo que ya usa IA, alineada con la política de datos — no un curso genérico de prompts.',
      ],
      extra: (
        <p className="sector-extra">
          Ese despliegue es el de{' '}
          <Link to="/ia-privada">IA privada / soberana</Link>
          . Si ya tenéis un prototipo (ChatGPT, Cursor, no-code) y hay que llevarlo
          a un entorno seguro, ver{' '}
          <Link to="/servicios/ia-en-produccion">IA en producción</Link>
          . El cumplimiento de alfabetización está en{' '}
          <Link to="/cumplimiento-ia">Cumplimiento IA (Art. 4)</Link>.
        </p>
      ),
    },
  ];

  const proceso = [
    {
      paso: '1',
      titulo: 'Auditoría inicial',
      descripcion:
        'Dónde están los datos, qué categoría son, quién accede, qué copias existen y qué dice (o no dice) vuestra documentación RGPD / AI Act.',
    },
    {
      paso: '2',
      titulo: 'Plan realista',
      descripcion:
        'Priorizamos lo que reduce riesgo de verdad: accesos, backups, perímetro e IA. ENS u otras normas, solo si os aplican.',
    },
    {
      paso: '3',
      titulo: 'Medidas y operativa',
      descripcion:
        'Implementamos accesos, copias, actualizaciones y políticas. Si ya usáis Vulpin, partimos de ese expediente; si no, sobre lo que tenéis.',
    },
    {
      paso: '4',
      titulo: 'Formación del equipo',
      descripcion:
        'Concienciación y alfabetización para quien atiende, administra o dirige. Sin eso, la medida técnica se cae en una semana.',
    },
    {
      paso: '5',
      titulo: 'Revisión y evidencias',
      descripcion:
        'Pruebas de restauración, revisión de accesos y documentación que podéis enseñar a dirección, a un auditor o a la administración.',
    },
  ];

  const faqSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      url: PAGE_URL,
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    }),
    []
  );

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Ciberseguridad y cumplimiento normativo',
    description:
      'Auditoría de seguridad y cumplimiento (RGPD, AI Act, ENS si aplica), gestión de accesos, backups de continuidad y formación del equipo para organizaciones que tratan datos sensibles.',
    url: PAGE_URL,
    serviceType: 'Ciberseguridad y cumplimiento normativo',
    provider: {
      '@type': 'Organization',
      name: 'E-ASY',
      url: 'https://e-asy.es',
    },
    areaServed: [
      { '@type': 'AdministrativeArea', name: 'Canarias' },
      { '@type': 'Country', name: 'España' },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Inicio',
        item: 'https://e-asy.es/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Servicios',
        item: 'https://e-asy.es/servicios',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Ciberseguridad y cumplimiento',
        item: PAGE_URL,
      },
    ],
  };

  return (
    <>
      <SEO
        title="Ciberseguridad y cumplimiento RGPD | Datos sensibles y AI Act | E-ASY"
        description="Protege datos sensibles (menores, dependencia, salud, clientes) y cumple RGPD y AI Act sin frenar la operativa. Auditoría, accesos, backups y formación para empresas de toda España, desde la sede en Tenerife."
        keywords="ciberseguridad empresas Canarias, cumplimiento RGPD Tenerife, AI Act pymes, datos categoría especial, menores dependencia, ENS, auditoría seguridad, formación ciberseguridad, backups continuidad, IA on-premise RGPD"
        canonical={PAGE_URL}
      />
      <JsonLd id="jsonld-ciber-faq" data={faqSchema} />
      <JsonLd id="jsonld-ciber-service" data={serviceSchema} />
      <JsonLd id="jsonld-ciber-breadcrumb" data={breadcrumbSchema} />
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      <div className={`ciberseguridad-cumplimiento-page ${darkMode ? 'dark' : 'light'}`}>
        <section className="ciberseguridad-hero">
          <div className="ciberseguridad-hero-content">
            <div className="ciberseguridad-hero-text">
              <h1 className="ciberseguridad-hero-title">
                Ciberseguridad y cumplimiento
              </h1>
              <p className="ciberseguridad-hero-subtitle">
                Protege <strong>datos sensibles</strong> y cumple{' '}
                <strong>RGPD</strong> y <strong>AI Act</strong> sin frenar la
                operativa del día a día. No vendemos un pack de antivirus: ordenamos
                accesos, copias, perímetro y formación para que el expediente no se
                convierta en un problema legal o en un agujero.
              </p>
              <p className="ciberseguridad-hero-subtitle">
                Trabajamos con pymes y con entidades que tratan{' '}
                <strong>menores, dependencia, violencia de género y salud</strong>
                —a menudo sobre <strong>Vulpin</strong>—. Desde Tenerife, para
                Canarias y el resto de España.
              </p>
              <div className="ciberseguridad-hero-cta">
                <Link to="/contacto" className="ciberseguridad-hero-button primary">
                  Agenda una auditoría de seguridad gratuita
                </Link>
                <Link to="/servicios" className="ciberseguridad-hero-button secondary">
                  Ver todos los servicios
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="ciberseguridad-que-es">
          <div className="ciberseguridad-que-es-content">
            <div className="ciberseguridad-que-es-header">
              <h2 className="ciberseguridad-que-es-title">
                El problema no es “tener antivirus”
              </h2>
              <p className="ciberseguridad-que-es-subtitle">
                Empresas y organizaciones manejan <strong>datos de clientes</strong>,
                y a menudo de <strong>menores, dependencia o salud</strong>, sin la
                protección ni el cumplimiento adecuados. Cuentas compartidas, copias
                que nadie ha restaurado nunca, un Excel con historiales y la duda de
                si se puede usar una IA con esos datos.
              </p>
              <p className="ciberseguridad-que-es-subtitle">
                La normativa vigente —<strong>RGPD</strong> y, si usáis inteligencia
                artificial, el <strong>AI Act</strong>— no es un trámite de la web.
                Un incidente o una inspección no pregunta si teníais “soporte IT”:
                pregunta quién accedió, dónde estaba la copia y si el equipo sabía
                lo que no se puede pegar en un chat público.
              </p>
            </div>
          </div>
        </section>

        <section className="ciberseguridad-beneficios">
          <div className="ciberseguridad-beneficios-content">
            <div className="ciberseguridad-beneficios-header">
              <h2 className="ciberseguridad-beneficios-title">
                Qué cambia cuando se hace bien
              </h2>
              <p className="ciberseguridad-beneficios-subtitle">
                Cumplimiento y seguridad como un solo servicio: el mismo criterio
                para el expediente, el servidor y el equipo.
              </p>
            </div>
            <div className="ciberseguridad-beneficios-grid">
              {beneficios.map((beneficio) => (
                <div key={beneficio.titulo} className="ciberseguridad-beneficio-card">
                  <h3 className="beneficio-title">{beneficio.titulo}</h3>
                  <p className="beneficio-description">{beneficio.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="ciberseguridad-servicios">
          <div className="ciberseguridad-servicios-content">
            <div className="ciberseguridad-servicios-header">
              <h2 className="ciberseguridad-servicios-title">Qué incluye el servicio</h2>
              <p className="ciberseguridad-servicios-subtitle">
                Cuatro bloques. No es un catálogo de incidencias: es el perímetro,
                la norma y la gente que lo opera.
              </p>
            </div>
            <div className="ciberseguridad-servicios-grid">
              {serviciosIncluidos.map((servicio) => (
                <div key={servicio.titulo} className="ciberseguridad-servicio-card">
                  <h3 className="servicio-title">{servicio.titulo}</h3>
                  <p className="servicio-description">{servicio.descripcion}</p>
                  <div className="servicio-casos">
                    <h4 className="casos-title">Incluye:</h4>
                    <ul className="casos-list">
                      {servicio.caracteristicas.map((item) => (
                        <li key={item} className="caso-item">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="ciberseguridad-sectores">
          <div className="ciberseguridad-sectores-content">
            <div className="ciberseguridad-sectores-header">
              <h2 className="ciberseguridad-sectores-title">
                Casos de uso por sector
              </h2>
              <p className="ciberseguridad-sectores-subtitle">
                Tres contextos reales. El mismo pilar: datos que no se pueden
                tratar “como el resto”.
              </p>
            </div>
            <div className="ciberseguridad-sectores-grid ciberseguridad-casos-grid">
              {casosPorSector.map((sector) => (
                <article key={sector.titulo} className="ciberseguridad-sector-card">
                  <h3 className="sector-title">{sector.titulo}</h3>
                  <p className="sector-description">{sector.descripcion}</p>
                  <h4 className="casos-title">Casos concretos</h4>
                  <ul className="casos-list">
                    {sector.casos.map((caso) => (
                      <li key={caso} className="caso-item">
                        {caso}
                      </li>
                    ))}
                  </ul>
                  {sector.extra}
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ciberseguridad-proceso">
          <div className="ciberseguridad-proceso-content">
            <div className="ciberseguridad-proceso-header">
              <h2 className="ciberseguridad-proceso-title">Cómo lo hacemos</h2>
              <p className="ciberseguridad-proceso-subtitle">
                Auditoría, plan, medidas, equipo y evidencias. Sin roadmap eterno.
              </p>
            </div>
            <div className="ciberseguridad-proceso-timeline">
              {proceso.map((item) => (
                <div key={item.paso} className="proceso-step">
                  <div className="step-number">{item.paso}</div>
                  <div className="step-content">
                    <h3 className="step-title">{item.titulo}</h3>
                    <p className="step-description">{item.descripcion}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="ciberseguridad-faq">
          <div className="ciberseguridad-faq-content">
            <div className="ciberseguridad-faq-header">
              <h2 className="ciberseguridad-faq-title">Preguntas frecuentes</h2>
            </div>
            <div className="ciberseguridad-faq-list">
              {FAQ_ITEMS.map((item) => (
                <details key={item.question} className="ciberseguridad-faq-item">
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="ciberseguridad-cta">
          <div className="ciberseguridad-cta-content">
            <h2 className="ciberseguridad-cta-title">
              ¿Manejáis datos que no pueden fallar?
            </h2>
            <p className="ciberseguridad-cta-subtitle">
              Revisamos accesos, copias, perímetro y huecos de RGPD / AI Act. Sin
              postureo y sin convertirlo en un proyecto de dos años.
            </p>
            <div className="ciberseguridad-cta-buttons">
              <Link to="/contacto" className="ciberseguridad-cta-button primary">
                Agenda una auditoría de seguridad gratuita
              </Link>
              <Link to="/servicios" className="ciberseguridad-cta-button secondary">
                Ver todos los servicios
              </Link>
            </div>
          </div>
        </section>
      </div>
      <CoberturaNota />
      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default CiberseguridadCumplimientoPage;
